import {
  Avatar,
  Button,
  Paper,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListSubheader,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  CropSquare as CropSquareIcon,
  Place as PlaceIcon,
  ShowChart as ShowChartIcon,
} from '@material-ui/icons';
import _ from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Form } from 'react-final-form';
import { useSnackbar } from '../Snackbar';
import {
  CREATE_FEATURE,
  DELETE_FEATURE,
  FETCH_FEATURE,
  FETCH_FEATURE_SUCCESS,
  UPDATE_FEATURE,
  FETCH_PLANS,
} from '../../actions';
import { doesIdExist } from '../../apis/utilities';
import {
  featureSubtypeColours,
  featureSubtypeIcons,
  featureSubtypes,
} from '../../data/constants';
import { ConfirmationDialog } from '../dialogs';
import {
  Field,
  TextField,
  SelectField,
  ContentField,
  TypesField,
  SelectMultipleField,
  GeometryField,
  required,
} from '../fields';
import { RouteLeavingGuard } from '../controls';

const { maxUploadSize, personGroups } = window.config;

const useStyles = makeStyles((theme) => ({
  card: {
    margin: theme.spacing(1),
    minWidth: 240,
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: 200,
  },
  recurrence: {
    marginLeft: theme.spacing(1),
    width: '100%',
  },
  cardContent: {
    padding: 0,
  },
  section: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subHeader: {
    marginTop: 10,
  },
  editor: {
    width: '100%',
    marginBottom: 16,
  },
  edits: {
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2),
    },
  },
  header: {
    paddingLeft: 0,
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(1),
    },
  },
  delete: {
    color: theme.palette.error.main,
  },
  close: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  collections: {
    width: '100%',
  },
}));

const featureTypes = {
  perimeters: { name: 'Perimeter', icon: <CropSquareIcon /> },
  paths: { name: 'Path', icon: <ShowChartIcon /> },
  markers: { name: 'Marker', icon: <PlaceIcon /> },
};

const geoJsonTypes = {
  Perimeter: 'Polygon',
  Path: 'LineString',
  Marker: 'Point',
};

export default function Feature() {
  const { id, featureType } = useParams();
  const dispatch = useDispatch();
  const feature = useSelector((state) => state.features.feature, _.isEqual);
  const error = useSelector((state) => state.features.error);
  const collections = useSelector(
    (state) => state.collections.collectionNames,
    _.isEqual
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const classes = useStyles();
  const snackbar = useSnackbar();

  useEffect(() => {
    if (error) {
      snackbar.notify('error', error);
    }
  }, [error, snackbar]);

  useEffect(() => {
    if (id === 'new') {
      dispatch({
        type: FETCH_FEATURE_SUCCESS,
        payload: null,
      });
    } else {
      dispatch({
        type: FETCH_FEATURE,
        payload: id,
      });
    }
    dispatch({
      type: FETCH_PLANS,
    });
  }, [id, dispatch]);

  function handleDelete() {
    if (feature) {
      dispatch({
        type: DELETE_FEATURE,
        payload: encodeURIComponent(feature.identifier),
      });
    }
  }

  async function validate(values) {
    const errors = {};

    if (!feature) {
      if (values.identifier) {
        if (values.identifier === 'new') {
          errors.identifier = 'Invalid';
        }
        const exists = await doesIdExist('features', values.identifier);
        if (exists) {
          errors.identifier = 'Exists';
        }
      }
    }

    const blob = new Blob([JSON.stringify(values)], {
      type: 'application/json',
    });

    if (blob.size > maxUploadSize) {
      snackbar.notify('warning', 'Unable to save, content greater than 5MB');
      errors.description = 'Content greater than 5MB';
    }

    return errors;
  }

  function onSubmit(values) {
    if (feature) {
      dispatch({
        type: UPDATE_FEATURE,
        payload: values,
      });
    } else {
      dispatch({
        type: CREATE_FEATURE,
        payload: values,
      });
    }
  }

  return (
    <Form
      initialValues={feature || { type: featureTypes[featureType].name }}
      validate={validate}
      onSubmit={onSubmit}
      render={({ handleSubmit, form, submitting, dirty, pristine, values }) => (
        <form onSubmit={handleSubmit}>
          <Helmet>
            <title>
              IR3 | {featureTypes[featureType].name}
              {values.title ? ` | ${values.title}` : ''}
            </title>
          </Helmet>
          <Paper className={classes.card}>
            <CardHeader
              avatar={
                <Avatar
                  className={classes.avatar}
                  style={{
                    backgroundColor:
                      values.type === 'Perimeter' && values.subtype
                        ? featureSubtypeColours.perimeters[values.subtype]
                            .stroke
                        : null,
                  }}
                >
                  {values.type === 'Marker'
                    ? featureSubtypeIcons.markers[values.subtype] ||
                      featureSubtypeIcons.markers.default
                    : values.subtype ||
                      (values.type
                        ? _.find(_.values(featureTypes), (item) => {
                            return item.name === values.type;
                          }).icon
                        : '')}
                </Avatar>
              }
              title={values.title}
              subheader={values.identifier}
            />
            <CardContent className={classes.cardContent}>
              {values.created && (
                <div className={classes.edits}>
                  <Typography variant="caption">
                    {`Created by ${values.created.userId} ${moment(
                      values.created.time
                    ).format('DD/MM/YYYY, HH:mm')}`}
                  </Typography>
                </div>
              )}
              {values.lastEdit && (
                <div className={classes.edits}>
                  <Typography variant="caption">
                    {`Last edited by ${values.lastEdit.userId} ${moment(
                      values.lastEdit.time
                    ).format('DD/MM/YYYY, HH:mm')}`}
                  </Typography>
                </div>
              )}
              <List>
                <ListSubheader disableSticky>Key Information</ListSubheader>
                <ListItem>
                  <div className={classes.section}>
                    <Field
                      name="title"
                      component={TextField}
                      label="Title"
                      className={classes.textField}
                      validate={required}
                    />
                    <Field
                      name="identifier"
                      component={TextField}
                      label="Identifier"
                      className={classes.textField}
                      disabled={!!feature}
                    />
                    <Field
                      name="subtype"
                      component={SelectField}
                      label="Type"
                      className={classes.textField}
                      validate={required}
                      values={Object.entries(
                        featureSubtypes[featureType]
                      ).map((item) => ({ value: item[0], label: item[1] }))}
                    />
                  </div>
                </ListItem>
                <Divider />
                <ListSubheader disableSticky>Description</ListSubheader>
                <ListItem>
                  <Field
                    name="description"
                    component={ContentField}
                    className={classes.editor}
                  />
                </ListItem>
                <Divider />
                <ListSubheader disableSticky>Groups & Areas</ListSubheader>
                <ListItem>
                  <div className={classes.section}>
                    <Field
                      name="areas"
                      types={personGroups}
                      component={TypesField}
                    />
                  </div>
                </ListItem>
                <Divider />
                <ListSubheader disableSticky>Plans</ListSubheader>
                <ListItem>
                  <div className={classes.collections}>
                    <Field
                      name="collections"
                      fullWidth
                      component={SelectMultipleField}
                      suggestions={collections.map((collection) => ({
                        label: collection.title,
                        value: collection.identifier,
                      }))}
                    />
                  </div>
                </ListItem>
                <Divider />
                <ListSubheader disableSticky>Location</ListSubheader>
                <Field
                  name="geometry"
                  component={GeometryField}
                  geoType={
                    geoJsonTypes[values.type || featureTypes[featureType].name]
                  }
                  validate={required}
                />
              </List>
            </CardContent>
            <CardActions disableSpacing>
              <Button
                color="primary"
                type="submit"
                disabled={pristine || submitting}
              >
                Save
              </Button>
              <Button
                color="primary"
                disabled={pristine || submitting}
                onClick={form.reset}
              >
                Cancel
              </Button>
              <Button
                className={classes.delete}
                onClick={() => setDeleteOpen(true)}
                disabled={feature === null}
              >
                Delete
              </Button>
            </CardActions>
          </Paper>
          <ConfirmationDialog
            action="Delete"
            open={deleteOpen}
            itemId={values.title || values.identifier}
            onOk={handleDelete}
            onCancel={() => setDeleteOpen(false)}
          />
          <RouteLeavingGuard when={dirty} />
        </form>
      )}
    />
  );
}
